import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { RefreshCw, Clock, AlertTriangle, Save, TrendingUp, Upload, CheckCircle, Smartphone, X, Search } from 'lucide-react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import TeamLeaderRCTIEditor from '../components/TeamLeaderRCTIEditor';
import DriverAvatar, { fetchDriverPhotos } from '../components/DriverPhoto';
import { periodGrid } from '../utils/periods';

const DOWNTIME_CATEGORIES = ['Break Time', 'Depot Wait', 'Traffic Hold', 'System Delay', 'Vehicle Issue', 'Other'];

// ── Period generation (28-day periods, same anchor as Tax Invoices / RCTI) ────


function formatMediumStatic(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Walks out from TODAY's period (periodGrid), not from the anchor: a fixed
// -24…+6 window around 16 May 2026 ran out on 31 Oct 2026, hiding the current period.
function generatePeriods() {
  return periodGrid({ back: 24, forward: 6 });
}

const PERIODS = generatePeriods();

function findCurrentPeriod() {
  return PERIODS.find(p => p.current) || PERIODS[0];
}

// Convert "DD-Mon-YYYY" sheet date to YYYY-MM-DD for period matching
function sheetDateToISO(dateStr) {
  if (!dateStr) return '';
  const monthMap = { Jan:'01',Feb:'02',Mar:'03',Apr:'04',May:'05',Jun:'06',Jul:'07',Aug:'08',Sep:'09',Oct:'10',Nov:'11',Dec:'12' };
  const parts = dateStr.split('-');
  if (parts.length !== 3) return '';
  return `${parts[2]}-${monthMap[parts[1]] || '00'}-${parts[0].padStart(2,'0')}`;
}

function fmt(n) {
  if (n === undefined || n === null) return '$0.00';
  const abs = Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return n < 0 ? `-$${abs}` : `$${abs}`;
}

function fmtHrs(n) {
  if (!n && n !== 0) return '—';
  return Number(n).toFixed(2) + ' hrs';
}

function toISO(dateStr) {
  if (!dateStr) return '';
  const monthMap = { Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06', Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12' };
  const parts = dateStr.split('-');
  if (parts.length !== 3) return '';
  return `${parts[2]}-${monthMap[parts[1]] || '00'}-${parts[0].padStart(2, '0')}`;
}

function PphBadge({ pph, expected }) {
  if (!pph || !expected) return <span style={{ color: 'var(--hp-dim)', fontSize: '.72rem' }}>—</span>;
  const ratio = pph / expected;
  if (ratio >= 0.9) return <span style={{ color: 'var(--hp-green)', fontSize: '.72rem', fontWeight: 600 }}>{pph.toFixed(0)} p/hr ✓</span>;
  if (ratio >= 0.7) return <span style={{ color: 'var(--hp-amber)', fontSize: '.72rem', fontWeight: 600 }}>{pph.toFixed(0)} p/hr ⚠</span>;
  return <span style={{ color: 'var(--hp-red)', fontSize: '.72rem', fontWeight: 600 }}>{pph.toFixed(0)} p/hr ↓</span>;
}

function parseTimeToDecimal(timeStr) {
  if (!timeStr) return null;
  const parts = timeStr.split(':');
  if (parts.length < 2) return null;
  const h = parseInt(parts[0]) || 0;
  const m = parseInt(parts[1]) || 0;
  const s = parseInt(parts[2]) || 0;
  return h + m / 60 + s / 3600;
}

// Portal-captured worked hours for one Driver_Logs row. Prefer the driver-submitted
// Total Hours; otherwise derive from shift start → finish (net of a logged break).
function portalShiftHours(r) {
  const t = Number(r.totalHours) || 0;
  if (t > 0) return Math.round(t * 100) / 100;
  const a = parseTimeToDecimal(r.shiftStart), b = parseTimeToDecimal(r.finishTime);
  if (a == null || b == null) return 0;
  let h = b - a; if (h < 0) h += 24;
  const bs = parseTimeToDecimal(r.breakStart), be = parseTimeToDecimal(r.breakEnd);
  if (bs != null && be != null) { let brk = be - bs; if (brk < 0) brk += 24; if (brk > 0 && brk < h) h -= brk; }
  return Math.round(h * 100) / 100;
}

export default function HourlyDriversPage() {
  const { addNotification, sheetLoading } = useApp();
  const [loading, setLoading] = useState(false);
  const [showTL, setShowTL] = useState(false);
  const [saving, setSaving] = useState(null);
  const [savingDowntime, setSavingDowntime] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [period, setPeriod] = useState('');
  const [freshData, setFreshData] = useState(null);
  const [csvStatus, setCsvStatus] = useState(null); // { filename, records, corrected, fallback, dateRange }
  const [selectedPeriod, setSelectedPeriod] = useState(() => findCurrentPeriod());
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);

  // Downtime state per driver
  const [downtimeState, setDowntimeState] = useState({});

  // Portal Times — driver-captured shift start/finish from Driver_Logs (the portal),
  // a separate time-capture source to weigh against Scanner & Reported hours at
  // period end. One call returns every driver's portal rows for the whole period.
  const [showPortal, setShowPortal] = useState(false);
  const [portalTimes, setPortalTimes] = useState([]); // [{date,login,name,shiftStart,breakStart,breakEnd,finishTime,totalKm,totalHours}]
  const [portalLoading, setPortalLoading] = useState(false);
  const [photos, setPhotos] = useState({});
  const [portalFilter, setPortalFilter] = useState('');
  const [portalFrom, setPortalFrom] = useState('');
  const [portalTo, setPortalTo] = useState('');

  const fetchPortalTimes = useCallback(async () => {
    setPortalLoading(true);
    try {
      const res = await fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getPortalShiftTimes', startISO: selectedPeriod.startISO, endISO: selectedPeriod.endISO }),
      });
      const data = await res.json();
      setPortalTimes(data.success ? (data.rows || []) : []);
    } catch (err) {
      setPortalTimes([]);
    } finally {
      setPortalLoading(false);
    }
  }, [selectedPeriod]);

  // Load portal times on mount + whenever the period changes, so the Portal Hrs
  // column and the Portal Times window are ready regardless of the CSV/sheet load.
  useEffect(() => { fetchPortalTimes(); }, [fetchPortalTimes]);

  // Default the Portal Times date filter to the full selected period; reset on change.
  useEffect(() => { setPortalFrom(selectedPeriod.startISO); setPortalTo(selectedPeriod.endISO); }, [selectedPeriod]);

  // Read-only driver profile photos for the main driver list + Portal Times.
  useEffect(() => {
    const src = [...(portalTimes || []), ...(drivers || [])];
    const logins = [...new Set(src.map(x => (x.login || x.loginName || x.driver || x.driverLogin || '').toUpperCase()).filter(Boolean))];
    if (logins.length) fetchDriverPhotos(logins).then(p => setPhotos(prev => ({ ...p, ...prev })));
  }, [portalTimes, drivers]);

  // Portal hours per login for the period (sum of completed shifts) + daily detail.
  // A network member's days inside their membership window are billed on the PRINCIPAL's
  // RCTI (network_drivers.from_date/to_date — see sheets.js "Network membership windows").
  // Only the days outside it are the driver's own, so those are the only ones that may
  // count toward their Agreed Hours. VALBUENAS left Alanas after 3 Jul 2026: without this
  // the page offers his whole 207.43 hrs as agreed, which would price Alanas's three weeks
  // onto his RCTI a second time (a $9k floor against a $1.5k invoice).
  // Only a PARTIAL member is clipped — one whose window leaves part of this period uncovered.
  // A member covered for the whole period has no RCTI of their own (they're suppressed into the
  // principal's), so clipping would empty their row and drop them off the page entirely; they
  // stay exactly as they are today.
  const billsToSelf = useCallback((login, dateISO) => {
    const w = (freshData?.networkWindows || {})[(login || '').toUpperCase()];
    if (!w || !dateISO) return true;                       // not a network member → all his own
    const partial = (w.from && w.from > selectedPeriod.startISO) || (w.to && w.to < selectedPeriod.endISO);
    if (!partial) return true;
    const inWindow = (!w.from || dateISO >= w.from) && (!w.to || dateISO <= w.to);
    return !inWindow;
  }, [freshData, selectedPeriod]);

  const portalByLogin = useMemo(() => {
    const out = {};
    (portalTimes || []).forEach(r => {
      const key = (r.login || '').toUpperCase();
      if (!key) return;
      if (!billsToSelf(key, r.date)) return;
      if (!out[key]) out[key] = { hours: 0, days: 0, shifts: [] };
      const hrs = portalShiftHours(r);
      out[key].shifts.push({ ...r, _hrs: hrs });
      if (r.finishTime) { out[key].hours += hrs; out[key].days += 1; }
    });
    Object.values(out).forEach(v => {
      v.hours = Math.round(v.hours * 100) / 100;
      v.shifts.sort((a, b) => (a.date < b.date ? -1 : 1));
    });
    return out;
  }, [portalTimes, billsToSelf]);

  // Daily rows for the Portal Times window, grouped by driver (then chronological)
  // so one driver's shifts sit together and the filter can jump straight to them.
  const portalDaily = useMemo(() =>
    (portalTimes || []).map(r => ({ ...r, _hrs: portalShiftHours(r) }))
      .sort((a, b) => {
        const la = (a.login || ''), lb = (b.login || '');
        if (la !== lb) return la < lb ? -1 : 1;
        return a.date < b.date ? -1 : 1;
      }),
  [portalTimes]);

  // Scanner hours per LOGIN|date (from Wave_Activity / imported CSV), so portal
  // times can be compared day-by-day rather than only as a period total. Keys are
  // "LOGIN|YYYY-MM-DD"; value carries the day's scanner hours + start/finish window.
  //
  // Clipped to the selected period. The Wave feed is period-filtered at fetch, but
  // a manual CSV import carries whatever days the file holds, so without this clip
  // a driver's wider scanner history joins the day-by-day compare while portal hours
  // span only the period — making the net delta meaningless (REGMID2 read 635.05 hrs
  // against a real 170.33 for 14 Jun–11 Jul 2026). Both sources key on YYYY-MM-DD,
  // so the string compare is sound.
  const scannerDaily = useMemo(() => {
    const out = {};
    Object.entries(freshData?.duration || {}).forEach(([k, v]) => {
      const [login, date] = k.split('|');
      if (!login || !date) return;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
      if (date < selectedPeriod.startISO || date > selectedPeriod.endISO) return;
      if (!billsToSelf(login, date)) return;   // the principal's day, not his own
      out[login.toUpperCase() + '|' + date] = {
        hours: Math.round((v.hoursDecimal || 0) * 100) / 100,
        start: v.scanStart || v.startIndoor || '',
        finish: v.scanFinish || v.endOutdoor || v.endIndoor || '',
      };
    });
    return out;
  }, [freshData, selectedPeriod, billsToSelf]);

  // Day-by-day portal-vs-scanner comparison for one login: the union of every date
  // that has a portal shift or scanner activity, with the delta (portal − scanner).
  // Positive delta = portal is heavier than the scanner recorded that day.
  const buildDayCompare = useCallback((login) => {
    const key = (login || '').toUpperCase();
    const p = portalByLogin[key];
    const dates = new Set();
    (p?.shifts || []).forEach(s => { if (s.date) dates.add(s.date); });
    Object.keys(scannerDaily).forEach(k => { if (k.startsWith(key + '|')) dates.add(k.split('|')[1]); });
    const rows = [...dates].sort().map(date => {
      const shift = (p?.shifts || []).find(s => s.date === date);
      const scan = scannerDaily[key + '|' + date];
      const portalHrs = shift ? (shift._hrs || 0) : 0;
      const scanHrs = scan ? scan.hours : 0;
      const hasScan = !!scan;
      const delta = (shift && shift.finishTime && hasScan) ? Math.round((portalHrs - scanHrs) * 100) / 100 : null;
      return {
        date,
        portalStart: shift?.shiftStart || '',
        portalFinish: shift?.finishTime || '',
        portalOpen: !!shift && !shift.finishTime,
        portalHrs, hasPortal: !!shift,
        scanStart: scan?.start || '', scanFinish: scan?.finish || '',
        scanHrs, hasScan,
        delta,
      };
    });
    const totPortal = Math.round(rows.reduce((s, r) => s + (r.portalFinish ? r.portalHrs : 0), 0) * 100) / 100;
    const totScan = Math.round(rows.reduce((s, r) => s + r.scanHrs, 0) * 100) / 100;
    const netDelta = Math.round((totPortal - totScan) * 100) / 100;
    return { rows, totPortal, totScan, netDelta };
  }, [portalByLogin, scannerDaily]);

  // Export the day-by-day Portal-vs-Scanner comparison for whoever is currently in
  // view (respects the modal's login filter + date range) to a styled .xlsx — the
  // per-driver audit trail behind "portal times are heavier than scanner".
  const exportPortalCompare = useCallback(async () => {
    const q = portalFilter.trim().toUpperCase();
    const from = portalFrom || selectedPeriod.startISO;
    const to = portalTo || selectedPeriod.endISO;
    const logins = [...new Set(portalDaily
      .filter(r => (!q || (r.login || '').toUpperCase().includes(q)))
      .map(r => (r.login || '').toUpperCase()).filter(Boolean))].sort();
    if (!logins.length) { addNotification('No drivers match the current filter to export', 'error'); return; }

    const XS = await import('xlsx-js-style');
    const HEAD = ['Driver', 'Name', 'Date', 'Portal Start', 'Portal Finish', 'Portal Hrs', 'Scanner Start', 'Scanner Finish', 'Scanner Hrs', 'Δ (Portal − Scanner)', 'Note'];
    const aoa = [HEAD];
    const styleRows = []; // parallel meta for fills: { tot?:bool, delta:number|null }
    styleRows.push({ head: true });

    let gPortal = 0, gScan = 0;
    logins.forEach(login => {
      const name = (portalByLogin[login]?.shifts?.[0]?.name) || '';
      const { rows, totPortal, totScan, netDelta } = buildDayCompare(login);
      const inRange = rows.filter(r => r.date >= from && r.date <= to);
      if (!inRange.length) return;
      inRange.forEach(r => {
        const note = r.portalOpen ? 'portal shift open (no finish)'
          : !r.hasScan ? 'no scanner activity'
          : !r.hasPortal ? 'no portal log'
          : r.delta != null && r.delta >= 0.5 ? 'portal heavier'
          : r.delta != null && r.delta <= -0.5 ? 'scanner heavier' : '';
        aoa.push([
          login, name, r.date,
          r.portalStart || '', r.portalOpen ? 'open' : (r.portalFinish || ''),
          r.hasPortal && r.portalFinish ? r.portalHrs : '',
          r.scanStart || '', r.scanFinish || '',
          r.hasScan ? r.scanHrs : '',
          r.delta == null ? '' : r.delta,
          note,
        ]);
        styleRows.push({ delta: r.delta });
      });
      // Per-driver subtotal
      const pTot = Math.round(totPortal * 100) / 100, sTot = Math.round(totScan * 100) / 100;
      aoa.push([login, name, 'TOTAL', '', '', pTot, '', '', sTot, netDelta, `${inRange.length} day${inRange.length === 1 ? '' : 's'}`]);
      styleRows.push({ tot: true, delta: netDelta });
      gPortal += pTot; gScan += sTot;
    });
    if (aoa.length <= 1) { addNotification('No dated rows to export for the current filter', 'error'); return; }
    if (logins.length > 1) {
      gPortal = Math.round(gPortal * 100) / 100; gScan = Math.round(gScan * 100) / 100;
      aoa.push(['ALL', `${logins.length} drivers`, 'GRAND TOTAL', '', '', gPortal, '', '', gScan, Math.round((gPortal - gScan) * 100) / 100, '']);
      styleRows.push({ tot: true, grand: true, delta: Math.round((gPortal - gScan) * 100) / 100 });
    }

    const ws = XS.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 12 }, { wch: 20 }, { wch: 12 }, { wch: 11 }, { wch: 12 }, { wch: 9 }, { wch: 12 }, { wch: 13 }, { wch: 10 }, { wch: 18 }, { wch: 22 }];
    const LAST = HEAD.length - 1;
    const thin = { style: 'thin', color: { rgb: 'CBD5E1' } };
    for (let r = 0; r < aoa.length; r++) {
      const meta = styleRows[r] || {};
      for (let c = 0; c <= LAST; c++) {
        const cell = ws[XS.utils.encode_cell({ r, c })];
        if (!cell) continue;
        const border = { top: thin, bottom: thin, left: thin, right: thin };
        if (meta.head) { cell.s = { font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 }, fill: { fgColor: { rgb: '16202B' } }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border }; continue; }
        // Colour the delta column by direction/severity
        let fill = null;
        if (c === 9 && typeof meta.delta === 'number') {
          if (meta.delta >= 0.5) fill = 'F4B6B6';       // portal materially heavier → red-ish
          else if (meta.delta <= -0.5) fill = 'BFE0F4';  // scanner heavier → blue-ish
        }
        if (meta.grand) fill = fill || 'E9EDF1';
        else if (meta.tot && !fill) fill = 'F1F5F9';
        cell.s = {
          border,
          font: { bold: !!meta.tot },
          alignment: { horizontal: c <= 2 ? 'left' : 'center', vertical: 'center' },
          ...(fill ? { fill: { fgColor: { rgb: fill } } } : {}),
        };
      }
    }
    XS.utils.sheet_add_aoa(ws, [[]], { origin: -1 });
    const wb = XS.utils.book_new();
    XS.utils.book_append_sheet(wb, ws, 'Portal vs Scanner');
    const out = XS.write(wb, { type: 'array', bookType: 'xlsx' });
    const blob = new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `portal-vs-scanner-${logins.length === 1 ? logins[0] : 'all'}-${from}_to_${to}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    addNotification(`Exported portal-vs-scanner comparison (${logins.length} driver${logins.length === 1 ? '' : 's'})`, 'success');
  }, [portalDaily, portalByLogin, buildDayCompare, portalFilter, portalFrom, portalTo, selectedPeriod, addNotification]);

  const buildDriverRows = (data, endDateStr) => {
    const endISO = toISO(endDateStr);
    const endDate = new Date(endISO + 'T23:59:59');
    const startDate = new Date(endDate);
    startDate.setDate(endDate.getDate() - 27);
    startDate.setHours(0, 0, 0, 0);

    const hourlyDrivers = (data.drivers || []).filter(d =>
      (d.type || '').toUpperCase() === 'HOURLY'
    );

    return hourlyDrivers.map(d => {
      const key = d.loginName.toUpperCase();
      // Same reason as billsToSelf: a partial network member's own parcels are the
      // out-of-window ones only. quantities[] is his whole-period total, which includes
      // the days that bill to his principal.
      const qty = (data.networkMemberOwnQuantities || {})[key]
        || data.quantities[key] || { Parcel: 0, Exp: 0, 'Depot Bags': 0, DOE: 0 };
      const gccdRates = data.gccdRates[key] || {};
      const hourlyRate = gccdRates.minHourlyRate || 0;

      const scannerHours = Object.entries(data.duration || {})
        .filter(([k]) => k.startsWith(key + '|'))
        .reduce((sum, [k, v]) => {
          const datePart = k.split('|')[1];
          if (!datePart) return sum;
          const entryDate = new Date(datePart + 'T12:00:00');
          if (entryDate >= startDate && entryDate <= endDate) {
            return sum + (v.hoursDecimal || 0);
          }
          return sum;
        }, 0);

      const existingInput = (data.hourlyInput || []).find(r =>
        (r.Login || '').toUpperCase() === key && r.Period === endDateStr
      );

      const existingDowntime = (data.downtimeLog || []).find(r =>
        (r.Login || '').toUpperCase() === key && r.Period === endDateStr
      );

      const reportedHours = existingInput ? parseFloat(existingInput['Reported Hours']) || 0 : 0;
      const agreedHours = existingInput ? parseFloat(existingInput['Agreed Hours']) || 0 : 0;
      const effectiveHours = agreedHours || reportedHours;
      const totalParcels = (qty.Parcel || 0) + (qty.Exp || 0);
      const pph = effectiveHours > 0 ? totalParcels / effectiveHours : 0;
      const onboarded = Math.round((data.onboarded || {})[key] || 0);
      const onboardedPph = effectiveHours > 0 ? onboarded / effectiveHours : 0;
      const hourlyAmt = effectiveHours * hourlyRate;

      const roundedScanner = Math.round(scannerHours * 100) / 100;
      const hoursVariance = reportedHours > 0 ? Math.round((reportedHours - roundedScanner) * 100) / 100 : 0;

      // Downtime applies when scanner > reported
      const downtimeHrs = reportedHours > 0 && roundedScanner > reportedHours
        ? Math.round((roundedScanner - reportedHours) * 100) / 100
        : 0;
      const downtimePct = roundedScanner > 0 ? Math.round((downtimeHrs / roundedScanner) * 10000) / 100 : 0;

      return {
        ...d,
        key,
        qty,
        hourlyRate,
        scannerHours: roundedScanner,
        reportedHours,
        agreedHours,
        adjustedHours: agreedHours || reportedHours,
        notes: existingInput?.Notes || '',
        totalParcels,
        pph: Math.round(pph * 10) / 10,
        onboarded,
        onboardedPph: Math.round(onboardedPph * 10) / 10,
        hourlyAmt: Math.round(hourlyAmt * 100) / 100,
        hoursVariance,
        // Scenario A: scanner > reported → downtime
        hasDowntime: downtimeHrs > 0,
        downtimeHrs,
        downtimePct,
        existingDowntimeCategory: existingDowntime?.Category || '',
        existingDowntimeNotes: existingDowntime?.Notes || '',
        // Scenario B: reported > scanner → inflation risk
        flagged: hoursVariance > 0.5,
        variancePct: roundedScanner > 0 ? Math.round((hoursVariance / roundedScanner) * 10000) / 100 : 0,
      };
    });
  };

  const importData = async () => {
    setLoading(true);
    try {
      const { fetchAllSheetData } = await import('../services/googleSheets');
      const data = await fetchAllSheetData(selectedPeriod.endISO);

      // Scanner hours: pull from the shared Wave_Activity that the Driver Activity
      // Summary page saves to the engine, so hours flow through here automatically
      // (no separate CSV import needed). Sum corrected hours per login|date — the
      // same basis that page totals on. Wave is the source of truth for a day it
      // covers; a later manual CSV import still overrides both.
      let waveCount = 0;
      try {
        const waveRes = await fetch('/.netlify/functions/appsscript', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'getWaveActivity', periodStart: selectedPeriod.startISO, periodEnd: selectedPeriod.endISO }),
        });
        const wave = await waveRes.json();
        if (wave.success && Array.isArray(wave.rows)) {
          const durFromWave = {};
          const onboardedFromWave = {};   // login -> total onboarded parcels (period)
          wave.rows.forEach(r => {
            const login = (r.login || '').toUpperCase();
            const date = (r.date || '').toString().trim();
            if (!login || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
            if (date < selectedPeriod.startISO || date > selectedPeriod.endISO) return;
            const hrs = Number(r.correctedHours) || 0;
            if (hrs > 0) {
              const k = login + '|' + date;
              if (!durFromWave[k]) durFromWave[k] = { driver: login, date, hoursDecimal: 0 };
              durFromWave[k].hoursDecimal += hrs;
              // Keep the day's scanner window (earliest indoor start → latest finish)
              // so the Portal Times panel can line portal hours up against scanner
              // hours day-by-day, not just as a period total.
              const s = (r.startIndoor || '').toString().trim();
              const f = (r.endOutdoor || r.endIndoor || '').toString().trim();
              if (s && (!durFromWave[k].scanStart || s < durFromWave[k].scanStart)) durFromWave[k].scanStart = s;
              if (f && (!durFromWave[k].scanFinish || f > durFromWave[k].scanFinish)) durFromWave[k].scanFinish = f;
            }
            onboardedFromWave[login] = (onboardedFromWave[login] || 0) + (Number(r.onboardItems) || 0);
          });
          waveCount = Object.keys(durFromWave).length;
          data.duration = { ...(data.duration || {}), ...durFromWave };
          data.onboarded = onboardedFromWave;
        }
      } catch (e) { /* non-fatal — Scanner Hrs stay 0; Import CSV still available */ }

      setFreshData(data);
      // Prefer the selected period; fall back to whatever Controls tab says
      const sheetEndISO = sheetDateToISO(data.controls.reportEndDate);
      const endDateStr = data.controls.reportEndDate && sheetEndISO === selectedPeriod.endISO
        ? data.controls.reportEndDate
        : (() => {
            const [y, m, d] = selectedPeriod.endISO.split('-').map(Number);
            const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            return `${d}-${months[m-1]}-${y}`;
          })();
      setPeriod(endDateStr);
      const rows = buildDriverRows(data, endDateStr);
      setDrivers(rows);

      // Init downtime state from existing log
      const dtState = {};
      rows.forEach(d => {
        dtState[d.key] = {
          category: d.existingDowntimeCategory || '',
          notes: d.existingDowntimeNotes || '',
        };
      });
      setDowntimeState(dtState);

      addNotification(
        `Loaded ${rows.length} hourly drivers for period ending ${endDateStr}` +
        (waveCount ? ' · scanner hours from activity data' : ' · no activity hours found — import the AP CSV on Driver Activity Summary'),
        'success'
      );
    } catch (err) {
      addNotification(`Load failed: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCSVImport = async (file) => {
    if (!file) return;
    let data = freshData;
    if (!data) {
      setLoading(true);
      try {
        const { fetchAllSheetData } = await import('../services/googleSheets');
        data = await fetchAllSheetData(selectedPeriod.endISO);
        setFreshData(data);
        const sheetEndISO = sheetDateToISO(data.controls.reportEndDate);
        const endDateStr = data.controls.reportEndDate && sheetEndISO === selectedPeriod.endISO
          ? data.controls.reportEndDate
          : (() => {
              const [y, m, d] = selectedPeriod.endISO.split('-').map(Number);
              const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
              return `${d}-${months[m-1]}-${y}`;
            })();
        setPeriod(endDateStr);
      } catch (err) {
        addNotification(`Failed to load sheet data: ${err.message}`, 'error');
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    }
    try {
      const buffer = await file.arrayBuffer();
      const decoder = new TextDecoder('utf-16le');
      let text = decoder.decode(buffer);
      if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);

      const lines = text.split(/\r?\n/).filter(l => l.trim());
      const headers = lines[0].split('\t').map(h => h.trim());

      const col = name => headers.indexOf(name);
      const iLogin = col('User Logon Id');
      const iDate  = col('Event Date');
      const iSI    = col('Start Indoor');
      const iEI    = col('End Indoor');
      const iEO    = col('End Outdoor');

      if (iLogin < 0 || iDate < 0 || iEO < 0) {
        addNotification('CSV missing expected columns (User Logon Id, Event Date, End Outdoor)', 'error');
        return;
      }

      // Build usual-start map from Drivers tab
      const usualStartMap = {};
      (data.drivers || []).forEach(d => {
        if (d.loginName && d.indoorStart) {
          usualStartMap[d.loginName.toUpperCase()] = d.indoorStart;
        }
      });

      const csvDuration = {};
      let corrected = 0, fallback = 0;
      const dates = [];

      lines.slice(1).forEach(line => {
        const vals = line.split('\t');
        const login   = (vals[iLogin] || '').trim().toUpperCase();
        const date    = (vals[iDate]  || '').trim();
        const startCSV = iSI >= 0 ? (vals[iSI] || '').trim() : '';
        const endIn    = iEI >= 0 ? (vals[iEI] || '').trim() : '';
        const endOut   = (vals[iEO] || '').trim();

        // Skip totals / summary rows
        if (!login || login === 'TOTAL' || !date || date === 'TOTAL' || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return;

        const endTime = endOut || endIn;
        if (!endTime) return;

        const usualStart = usualStartMap[login];
        const effectiveStart = usualStart || startCSV;
        if (!effectiveStart) return;

        const startDec = parseTimeToDecimal(effectiveStart);
        const endDec   = parseTimeToDecimal(endTime);
        if (startDec === null || endDec === null) return;

        let hrs = endDec - startDec;
        if (hrs < 0) hrs += 24;
        // Safety clamp — no single shift should exceed 16 hrs
        hrs = Math.min(Math.round(hrs * 100) / 100, 16);

        csvDuration[`${login}|${date}`] = {
          driver: login,
          date,
          hoursDecimal: hrs,
          endOutdoor: endOut,
          startIndoor: effectiveStart,
          endIndoor: endIn,
          csvStartIndoor: startCSV,
          usualStartUsed: !!usualStart,
        };

        if (usualStart) corrected++; else fallback++;
        if (!dates.includes(date)) dates.push(date);
      });

      const totalRecords = corrected + fallback;
      if (totalRecords === 0) {
        addNotification('No valid driver rows found in CSV — check the file format.', 'error');
        return;
      }

      // Merge: CSV values override the Wave feed
      const mergedDuration = { ...(data.duration || {}), ...csvDuration };
      const mergedData = { ...data, duration: mergedDuration };
      setFreshData(mergedData);

      const activePeriod = period || (() => {
        const [y, m, d2] = selectedPeriod.endISO.split('-').map(Number);
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return `${d2}-${months[m-1]}-${y}`;
      })();
      if (!period) setPeriod(activePeriod);

      const newRows = buildDriverRows(mergedData, activePeriod);
      setDrivers(newRows);
      const dtState = {};
      newRows.forEach(d => {
        dtState[d.key] = {
          category: d.existingDowntimeCategory || '',
          notes: d.existingDowntimeNotes || '',
        };
      });
      setDowntimeState(dtState);

      dates.sort();
      setCsvStatus({
        filename: file.name,
        records: totalRecords,
        corrected,
        fallback,
        dateRange: dates.length > 1 ? `${dates[0]} → ${dates[dates.length - 1]}` : dates[0] || '',
      });
      addNotification(`CSV imported: ${totalRecords} records, ${corrected} start times corrected from Drivers tab`, 'success');
    } catch (err) {
      addNotification(`CSV import failed: ${err.message}`, 'error');
    }
  };

  const onDragEnter = (e) => {
    e.preventDefault();
    dragCounter.current++;
    if (e.dataTransfer.types.includes('Files')) setDragging(true);
  };
  const onDragLeave = () => {
    dragCounter.current--;
    if (dragCounter.current === 0) setDragging(false);
  };
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleCSVImport(file);
  };

  const availablePeriods = freshData
    ? [...new Set([
        freshData.controls.reportEndDate,
        ...(freshData.hourlyInput || []).map(r => r.Period).filter(Boolean)
      ])].filter(Boolean).sort().reverse()
    : [];

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    if (freshData) {
      const rows = buildDriverRows(freshData, newPeriod);
      setDrivers(rows);
      const dtState = {};
      rows.forEach(d => {
        dtState[d.key] = {
          category: d.existingDowntimeCategory || '',
          notes: d.existingDowntimeNotes || '',
        };
      });
      setDowntimeState(dtState);
    }
  };

  const updateDriver = (key, field, value) => {
    setDrivers(prev => prev.map(d => {
      if (d.key !== key) return d;
      const updated = { ...d, [field]: field === 'notes' ? value : parseFloat(value) || 0 };
      const agreedHours = field === 'adjustedHours' ? parseFloat(value) || 0 : d.adjustedHours;
      const reportedHours = field === 'reportedHours' ? parseFloat(value) || 0 : d.reportedHours;
      const effectiveHours = agreedHours || reportedHours;
      const totalParcels = (d.qty.Parcel || 0) + (d.qty.Exp || 0);
      updated.pph = effectiveHours > 0 ? Math.round((totalParcels / effectiveHours) * 10) / 10 : 0;
      updated.onboardedPph = effectiveHours > 0 ? Math.round(((d.onboarded || 0) / effectiveHours) * 10) / 10 : 0;
      updated.hourlyAmt = Math.round(effectiveHours * d.hourlyRate * 100) / 100;
      updated.hoursVariance = reportedHours > 0 ? Math.round((reportedHours - d.scannerHours) * 100) / 100 : 0;
      updated.variancePct = d.scannerHours > 0 ? Math.round((updated.hoursVariance / d.scannerHours) * 10000) / 100 : 0;
      updated.flagged = updated.hoursVariance > 0.5;
      // Recalc downtime
      updated.downtimeHrs = reportedHours > 0 && d.scannerHours > reportedHours
        ? Math.round((d.scannerHours - reportedHours) * 100) / 100 : 0;
      updated.downtimePct = d.scannerHours > 0 ? Math.round((updated.downtimeHrs / d.scannerHours) * 10000) / 100 : 0;
      updated.hasDowntime = updated.downtimeHrs > 0;
      updated.dirty = true; // edited since last save — drives the unsaved indicator
      return updated;
    }));
  };

  const updateDowntime = (key, field, value) => {
    setDowntimeState(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const saveDriver = async (driver) => {
    setSaving(driver.key);
    try {
      const payload = {
        action: 'saveHourlyInput',
        login: driver.loginName,
        period,
        reportedHours: driver.reportedHours,
        scannerHours: driver.scannerHours,
        agreedHours: driver.adjustedHours,
        notes: driver.notes,
      };
      const res = await fetch('/.netlify/functions/appsscript', { method: 'POST', body: JSON.stringify(payload) });
      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'save failed');
      setDrivers(prev => prev.map(d => d.key === driver.key ? { ...d, agreedHours: d.adjustedHours, dirty: false } : d));
      addNotification(`Saved ${driver.loginName} — agreed hours ${driver.adjustedHours || 0}`, 'success');
    } catch (err) {
      addNotification(`Save failed: ${err.message}`, 'error');
    } finally {
      setSaving(null);
    }
  };

  const saveDowntime = async (driver) => {
    const dt = downtimeState[driver.key] || {};
    if (!dt.category) {
      addNotification('Please select a downtime category first', 'error');
      return;
    }
    setSavingDowntime(driver.key);
    try {
      const payload = {
        action: 'saveDowntime',
        login: driver.loginName,
        period,
        scannerHrs: driver.scannerHours,
        reportedHrs: driver.reportedHours,
        downtimeHrs: driver.downtimeHrs,
        downtimePct: driver.downtimePct,
        category: dt.category,
        notes: dt.notes || '',
      };
      const res = await fetch('/.netlify/functions/downtime-sb', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const result = await res.json();
      if (!result.success) throw new Error(result.error || 'save failed');
      addNotification(`Downtime saved for ${driver.loginName}`, 'success');
    } catch (err) {
      addNotification(`Downtime save failed: ${err.message}`, 'error');
    } finally {
      setSavingDowntime(null);
    }
  };

  const flaggedCount = drivers.filter(d => d.flagged).length;
  const downtimeCount = drivers.filter(d => d.hasDowntime).length;
  const dirtyCount = drivers.filter(d => d.dirty).length;
  const totalHourlyAmt = drivers.reduce((s, d) => s + d.hourlyAmt, 0);
  const totalParcels = drivers.reduce((s, d) => s + d.totalParcels, 0);

  // Warn before leaving the tab/page while edits are unsaved (agreed hours feed RCTI).
  useEffect(() => {
    if (dirtyCount === 0) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirtyCount]);

  return (
    <React.Fragment>
      {dragging && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(26,58,107,.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <div style={{
            background: 'var(--hp-surface)', border: '2px dashed var(--hp-gold)',
            borderRadius: 16, padding: '48px 64px', textAlign: 'center',
          }}>
            <Upload size={40} color="var(--hp-gold)" style={{ marginBottom: 12 }} />
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--hp-gold)' }}>Drop CSV to import</div>
          </div>
        </div>
      )}
    <div
      style={{ display: 'contents' }}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div className="page-header">
        <div className="page-header-left">
          <h2>Hourly Drivers</h2>
          <p>Scanner hours vs reported hours · Parcels/hr performance · Agreed hours for RCTI</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            className="form-select"
            style={{ minWidth: 260 }}
            value={selectedPeriod.endISO}
            onChange={e => {
              const p = PERIODS.find(p => p.endISO === e.target.value);
              if (p) { setSelectedPeriod(p); setFreshData(null); setDrivers([]); setCsvStatus(null); }
            }}
          >
            {PERIODS.map(p => (
              <option key={p.endISO} value={p.endISO}>{p.label}</option>
            ))}
          </select>
          <button className="btn btn-secondary btn-sm" onClick={() => fileInputRef.current?.click()} disabled={loading}>
            <Upload size={13} /> Import CSV
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.tsv,.txt"
            style={{ display: 'none' }}
            onChange={e => { handleCSVImport(e.target.files[0]); e.target.value = ''; }}
          />
          <button className="btn btn-primary btn-sm" onClick={importData} disabled={loading || sheetLoading}>
            {loading
              ? <React.Fragment><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> Loading...</React.Fragment>
              : <React.Fragment><Clock size={13} /> Load Hourly Data</React.Fragment>}
          </button>
          <button className={`btn btn-sm ${showPortal ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setShowPortal(v => !v)}>
            <Smartphone size={13} /> Portal Times
          </button>
          <button className={`btn btn-sm ${showTL ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setShowTL(v => !v)}>
            <TrendingUp size={13} /> Team Leader RCTI
          </button>
        </div>
      </div>

      <div className="page-body">
        {showTL && <TeamLeaderRCTIEditor period={selectedPeriod} />}

        {showPortal && (() => {
          const q = portalFilter.trim().toLowerCase();
          const rows = portalDaily.filter(r =>
            (!q || (r.login || '').toLowerCase().includes(q)) &&
            (!portalFrom || r.date >= portalFrom) &&
            (!portalTo || r.date <= portalTo)
          );
          const dateNarrowed = portalFrom > selectedPeriod.startISO || portalTo < selectedPeriod.endISO;
          const totHrs = Math.round(rows.reduce((s, r) => s + (r.finishTime ? r._hrs : 0), 0) * 100) / 100;
          const totKm = Math.round(rows.reduce((s, r) => s + (Number(r.totalKm) || 0), 0));
          // Per-row scanner lookup + running scanner total, so the window shows the
          // day-by-day portal-vs-scanner delta (the whole point of the comparison).
          const scanFor = r => scannerDaily[(r.login || '').toUpperCase() + '|' + r.date];
          const haveScanner = Object.keys(scannerDaily).length > 0;
          const totScan = Math.round(rows.reduce((s, r) => { const sc = scanFor(r); return s + (sc ? sc.hours : 0); }, 0) * 100) / 100;
          const netDelta = Math.round((totHrs - totScan) * 100) / 100;
          const drvCount = new Set(rows.map(r => (r.login || '').toUpperCase())).size;
          const completedDays = rows.filter(r => r.finishTime).length;
          const openCount = rows.length - completedDays;
          const avgHrs = completedDays ? totHrs / completedDays : 0;
          const single = drvCount === 1 && rows.length > 0 ? rows[0] : null;
          const dateSpan = rows.length
            ? { from: rows.reduce((m, r) => (r.date < m ? r.date : m), rows[0].date), to: rows.reduce((m, r) => (r.date > m ? r.date : m), rows[0].date) }
            : null;
          const stickyTh = { position: 'sticky', top: 0, background: 'var(--hp-surface)', zIndex: 1 };
          const stickyBottom = { position: 'sticky', bottom: 0, background: 'var(--hp-surface)', borderTop: '2px solid var(--hp-border)', zIndex: 1 };
          return (
            <div
              onClick={() => setShowPortal(false)}
              style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,42,.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 20px', overflowY: 'auto' }}
            >
              <div
                onClick={e => e.stopPropagation()}
                className="card"
                style={{ width: '100%', maxWidth: 1080, maxHeight: '85vh', display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}
              >
                <div style={{ borderBottom: '1px solid var(--hp-border)', flexShrink: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '12px 16px 10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Smartphone size={15} color="var(--hp-blue)" />
                      <span style={{ fontWeight: 700 }}>Portal Times</span>
                      <span style={{ fontSize: '.75rem', color: 'var(--hp-dim)' }}>
                        Driver-captured shift start &amp; finish from the portal · {selectedPeriod.label}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button className="btn btn-secondary btn-sm" disabled={portalLoading} onClick={fetchPortalTimes}>
                        {portalLoading
                          ? <React.Fragment><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</React.Fragment>
                          : <React.Fragment><RefreshCw size={12} /> Refresh</React.Fragment>}
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={exportPortalCompare} disabled={rows.length === 0}
                        title={haveScanner ? 'Download day-by-day portal-vs-scanner comparison (.xlsx)' : 'Load data first to include scanner hours'}>
                        <Upload size={12} style={{ transform: 'rotate(180deg)' }} /> Export
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setShowPortal(false)} title="Close">
                        <X size={14} /> Close
                      </button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '0 16px 12px' }}>
                    <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 380 }}>
                      <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--hp-dim)' }} />
                      <input
                        autoFocus
                        value={portalFilter}
                        onChange={e => setPortalFilter(e.target.value)}
                        placeholder="Filter by driver login…"
                        style={{ width: '100%', boxSizing: 'border-box', padding: '8px 30px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '.82rem', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}
                      />
                      {portalFilter && (
                        <button onClick={() => setPortalFilter('')} title="Clear filter"
                          style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-dim)', display: 'flex' }}>
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    <label style={{ fontSize: '.72rem', color: 'var(--hp-dim)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      From
                      <input type="date" value={portalFrom} min={selectedPeriod.startISO} max={portalTo || selectedPeriod.endISO}
                        onChange={e => setPortalFrom(e.target.value)}
                        style={{ padding: '7px 8px', borderRadius: 7, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '.78rem', fontFamily: 'inherit' }} />
                    </label>
                    <label style={{ fontSize: '.72rem', color: 'var(--hp-dim)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      To
                      <input type="date" value={portalTo} min={portalFrom || selectedPeriod.startISO} max={selectedPeriod.endISO}
                        onChange={e => setPortalTo(e.target.value)}
                        style={{ padding: '7px 8px', borderRadius: 7, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '.78rem', fontFamily: 'inherit' }} />
                    </label>
                    {dateNarrowed && (
                      <button className="btn btn-secondary btn-sm" onClick={() => { setPortalFrom(selectedPeriod.startISO); setPortalTo(selectedPeriod.endISO); }} title="Reset dates to full period">
                        <X size={13} /> Dates
                      </button>
                    )}
                    <span style={{ fontSize: '.78rem', color: 'var(--hp-dim)' }}>
                      <strong style={{ color: 'var(--hp-blue)' }}>{drvCount}</strong> {drvCount === 1 ? 'driver' : 'drivers'} · <strong style={{ color: 'var(--hp-blue)' }}>{rows.length}</strong> shifts · <strong style={{ color: 'var(--hp-blue)' }}>{fmtHrs(Math.round(totHrs * 100) / 100)}</strong>
                    </span>
                  </div>
                </div>
                {single && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 22, padding: '12px 18px', borderBottom: '1px solid var(--hp-border)', background: 'rgba(59,130,246,.05)', flexShrink: 0 }}>
                    <div style={{ minWidth: 150 }}>
                      <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '.95rem' }}>{single.login}</div>
                      {single.name && <div style={{ fontSize: '.75rem', color: 'var(--hp-dim)' }}>{single.name}</div>}
                      {dateSpan && <div style={{ fontSize: '.72rem', color: 'var(--hp-dim)', marginTop: 2 }}>{formatMediumStatic(dateSpan.from)} – {formatMediumStatic(dateSpan.to)}</div>}
                    </div>
                    {[
                      { label: 'Total portal hrs', value: fmtHrs(totHrs) },
                      ...(haveScanner ? [
                        { label: 'Scanner hrs', value: fmtHrs(totScan) },
                        { label: 'Net Δ portal − scanner', value: (netDelta >= 0 ? '+' : '') + netDelta.toFixed(2) + ' hrs', warn: netDelta >= 0.5, good: netDelta <= -0.5 },
                      ] : []),
                      { label: 'Days worked', value: completedDays },
                      { label: 'Avg hrs/day', value: avgHrs ? avgHrs.toFixed(2) : '—' },
                      { label: 'Total KM', value: totKm.toLocaleString() },
                      ...(openCount ? [{ label: 'Open (no finish)', value: openCount, warn: true }] : []),
                    ].map(s => (
                      <div key={s.label}>
                        <div style={{ fontSize: '.6rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--hp-dim)' }}>{s.label}</div>
                        <div style={{ fontSize: '1.15rem', fontWeight: 800, color: s.warn ? 'var(--hp-amber)' : s.good ? 'var(--hp-green)' : 'var(--hp-blue)' }}>{s.value}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ overflow: 'auto', flex: 1 }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={stickyTh}>Driver</th>
                        <th style={stickyTh}>Date</th>
                        <th style={stickyTh}>Portal Start</th>
                        <th style={stickyTh}>Portal Finish</th>
                        <th style={{ ...stickyTh, textAlign: 'right' }}>Portal Hrs</th>
                        <th style={{ ...stickyTh, textAlign: 'right' }} title="Scanner hours for the same day (load data to populate)">Scanner Hrs</th>
                        <th style={{ ...stickyTh, textAlign: 'right' }} title="Portal hours − scanner hours. Positive = portal is heavier.">Δ</th>
                        <th style={{ ...stickyTh, textAlign: 'right' }}>KM</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 && (
                        <tr><td colSpan={8} style={{ textAlign: 'center', padding: '28px 20px', color: 'var(--hp-dim)' }}>
                          {portalLoading ? 'Loading portal times…' : (q || dateNarrowed) ? 'No portal shifts match the current filter.' : 'No portal shifts captured for this period.'}
                        </td></tr>
                      )}
                      {rows.map((r, i) => {
                        const newDrv = i === 0 || rows[i - 1].login !== r.login;
                        const sc = scanFor(r);
                        const delta = (r.finishTime && sc) ? Math.round((r._hrs - sc.hours) * 100) / 100 : null;
                        const deltaColor = delta == null ? 'var(--hp-dim)' : delta >= 0.5 ? 'var(--hp-red)' : delta <= -0.5 ? 'var(--hp-blue)' : 'var(--hp-green)';
                        return (
                          <tr key={(r.login || '') + '|' + r.date} style={newDrv && i > 0 ? { borderTop: '2px solid var(--hp-border)' } : undefined}>
                            <td style={{ fontFamily: 'var(--font-mono)', fontSize: '.8rem', fontWeight: 600 }}>
                              {newDrv ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <DriverAvatar login={r.login} photo={photos[(r.login || '').toUpperCase()]} size={26} />
                                  <span>{r.login}</span>
                                </div>
                              ) : ''}
                            </td>
                            <td style={{ fontSize: '.8rem' }}>{formatMediumStatic(r.date)}</td>
                            <td style={{ fontSize: '.8rem' }}>{r.shiftStart || '—'}</td>
                            <td style={{ fontSize: '.8rem', color: r.finishTime ? 'var(--hp-text)' : 'var(--hp-amber)' }}>{r.finishTime || 'open'}</td>
                            <td style={{ fontSize: '.8rem', textAlign: 'right', fontWeight: 600 }}>{r._hrs > 0 ? fmtHrs(r._hrs) : '—'}</td>
                            <td style={{ fontSize: '.8rem', textAlign: 'right', color: sc ? 'var(--hp-text)' : 'var(--hp-dim)' }} title={sc && (sc.start || sc.finish) ? `${sc.start || '?'} → ${sc.finish || '?'}` : (haveScanner ? 'No scanner activity this day' : 'Load data to compare')}>{sc ? fmtHrs(sc.hours) : (haveScanner ? '—' : '·')}</td>
                            <td style={{ fontSize: '.8rem', textAlign: 'right', fontWeight: 700, color: deltaColor }}>{delta == null ? '—' : (delta >= 0 ? '+' : '') + delta.toFixed(2)}</td>
                            <td style={{ fontSize: '.8rem', textAlign: 'right', color: 'var(--hp-dim)' }}>{r.totalKm > 0 ? Math.round(r.totalKm) : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {rows.length > 0 && (
                      <tfoot>
                        <tr>
                          <td style={{ ...stickyBottom, fontWeight: 700 }}>Total</td>
                          <td style={{ ...stickyBottom, fontSize: '.78rem', color: 'var(--hp-dim)' }}>{rows.length} shift{rows.length === 1 ? '' : 's'}{openCount ? ` · ${openCount} open` : ''}</td>
                          <td style={stickyBottom}></td>
                          <td style={stickyBottom}></td>
                          <td style={{ ...stickyBottom, textAlign: 'right', fontWeight: 800, color: 'var(--hp-blue)' }}>{fmtHrs(totHrs)}</td>
                          <td style={{ ...stickyBottom, textAlign: 'right', fontWeight: 800 }}>{haveScanner ? fmtHrs(totScan) : '—'}</td>
                          <td style={{ ...stickyBottom, textAlign: 'right', fontWeight: 800, color: !haveScanner ? 'var(--hp-dim)' : netDelta >= 0.5 ? 'var(--hp-red)' : netDelta <= -0.5 ? 'var(--hp-blue)' : 'var(--hp-green)' }}>{haveScanner ? (netDelta >= 0 ? '+' : '') + netDelta.toFixed(2) : '—'}</td>
                          <td style={{ ...stickyBottom, textAlign: 'right', fontWeight: 700 }}>{totKm.toLocaleString()}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            </div>
          );
        })()}

        <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(5,1fr)', marginBottom: 20 }}>
          {[
            { label: 'Hourly Drivers', value: drivers.length, accent: 'var(--hp-gold)' },
            { label: 'Inflation Risk', value: flaggedCount, accent: flaggedCount > 0 ? 'var(--hp-amber)' : 'var(--hp-green)' },
            { label: 'Downtime Logged', value: downtimeCount, accent: downtimeCount > 0 ? 'var(--hp-blue)' : 'var(--hp-green)' },
            { label: 'Total Parcels', value: totalParcels.toLocaleString(), accent: 'var(--hp-blue)', sm: true },
            { label: 'Total Hourly Amt', value: fmt(totalHourlyAmt), accent: 'var(--hp-purple)', sm: true },
          ].map(s => (
            <div className="stat-card" key={s.label} style={{ '--accent': s.accent }}>
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ fontSize: s.sm ? '1.1rem' : '1.8rem' }}>{s.value}</div>
            </div>
          ))}
        </div>

        {dirtyCount > 0 && (
          <div style={{ background: 'rgba(217,119,6,.08)', border: '1px solid var(--hp-amber)', borderRadius: 8, padding: '10px 16px', marginBottom: 8, fontSize: '.82rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={15} color="var(--hp-amber)" />
            <span style={{ color: 'var(--hp-amber)', fontWeight: 700 }}>
              {dirtyCount} driver{dirtyCount > 1 ? 's have' : ' has'} unsaved changes
            </span>
            <span style={{ color: 'var(--hp-dim)' }}>— click Save on each row before leaving (agreed hours feed the RCTI).</span>
          </div>
        )}

        {csvStatus && (
          <div style={{ background: 'rgba(34,197,94,.06)', border: '1px solid rgba(34,197,94,.25)', borderRadius: 8, padding: '10px 16px', marginBottom: 8, fontSize: '.8rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle size={14} color="var(--hp-green)" />
            <span style={{ color: 'var(--hp-green)', fontWeight: 600 }}>CSV imported</span>
            <span style={{ color: 'var(--hp-dim)' }}>·</span>
            <span style={{ color: 'var(--hp-text)' }}>{csvStatus.filename}</span>
            <span style={{ color: 'var(--hp-dim)' }}>·</span>
            <span style={{ color: 'var(--hp-text)' }}>{csvStatus.dateRange}</span>
            <span style={{ color: 'var(--hp-dim)' }}>·</span>
            <span style={{ color: 'var(--hp-text)' }}>{csvStatus.records} records</span>
            {csvStatus.corrected > 0 && (
              <React.Fragment>
                <span style={{ color: 'var(--hp-dim)' }}>·</span>
                <span style={{ color: 'var(--hp-green)' }}>{csvStatus.corrected} start times corrected from Drivers tab</span>
              </React.Fragment>
            )}
            {csvStatus.fallback > 0 && (
              <React.Fragment>
                <span style={{ color: 'var(--hp-dim)' }}>·</span>
                <span style={{ color: 'var(--hp-amber)' }}>{csvStatus.fallback} used CSV start time (no usual start configured)</span>
              </React.Fragment>
            )}
            <button style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-dim)', fontSize: '1rem', lineHeight: 1 }} onClick={() => setCsvStatus(null)}>×</button>
          </div>
        )}

        {flaggedCount > 0 && (
          <div style={{ background: 'rgba(251,191,36,.06)', border: '1px solid rgba(251,191,36,.25)', borderRadius: 8, padding: '10px 16px', marginBottom: 8, fontSize: '.8rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={14} color="var(--hp-amber)" />
            <span style={{ color: 'var(--hp-amber)' }}>{flaggedCount} driver{flaggedCount > 1 ? 's' : ''} with reported hours exceeding scanner hours — review and provide reason.</span>
          </div>
        )}
        {downtimeCount > 0 && (
          <div style={{ background: 'rgba(59,130,246,.06)', border: '1px solid rgba(59,130,246,.25)', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: '.8rem', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Clock size={14} color="var(--hp-blue)" />
            <span style={{ color: 'var(--hp-blue)' }}>{downtimeCount} driver{downtimeCount > 1 ? 's' : ''} with scanner hours exceeding reported hours — downtime categorisation required.</span>
          </div>
        )}

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 28 }}></th>
                <th>Driver</th>
                <th>Company</th>
                <th>Parcels</th>
                <th>Scanner Hrs</th>
                <th>Portal Hrs</th>
                <th>Reported Hrs</th>
                <th>Agreed Hrs</th>
                <th>Variance</th>
                <th>Downtime</th>
                <th>P/Hr</th>
                <th>Hourly Amt</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {drivers.filter(driver => driver.totalParcels > 0).map(driver => (
                <React.Fragment key={driver.key}>
                  <tr
                    style={{ cursor: 'pointer', background: driver.flagged ? 'rgba(251,191,36,.04)' : driver.hasDowntime ? 'rgba(59,130,246,.03)' : undefined }}
                    onClick={() => setExpanded(expanded === driver.key ? null : driver.key)}
                  >
                    <td>{expanded === driver.key ? <ChevronDown size={13} color="var(--hp-gold)" /> : <ChevronRight size={13} color="var(--hp-dim)" />}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: '.8rem', fontWeight: 500 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <DriverAvatar login={driver.loginName} photo={photos[(driver.loginName || '').toUpperCase()]} size={26} />
                        <span>{driver.loginName}</span>
                      </div>
                    </td>
                    <td style={{ fontSize: '.8rem' }}>{driver.companyName || driver.name || '—'}</td>
                    <td style={{ fontWeight: 600, color: 'var(--hp-gold)' }}>{driver.totalParcels.toLocaleString()}</td>
                    <td style={{ fontSize: '.8rem', color: 'var(--hp-dim)' }}>{fmtHrs(driver.scannerHours)}</td>
                    <td style={{ fontSize: '.8rem', color: 'var(--hp-blue)' }}>
                      {(() => { const p = portalByLogin[driver.key]; return p && p.hours > 0
                        ? fmtHrs(p.hours)
                        : <span style={{ color: 'var(--hp-dim)' }}>{portalLoading ? '…' : '—'}</span>; })()}
                    </td>
                    <td style={{ fontSize: '.8rem' }}>{driver.reportedHours > 0 ? fmtHrs(driver.reportedHours) : <span style={{ color: 'var(--hp-dim)' }}>Not submitted</span>}</td>
                    <td style={{ fontWeight: 600 }}>{driver.adjustedHours > 0 ? fmtHrs(driver.adjustedHours) : <span style={{ color: 'var(--hp-dim)' }}>—</span>}</td>
                    <td>
                      {driver.reportedHours === 0
                        ? <span style={{ color: 'var(--hp-dim)', fontSize: '.76rem' }}>—</span>
                        : driver.flagged
                        ? <span style={{ color: 'var(--hp-amber)', fontSize: '.76rem', fontWeight: 600 }}>+{driver.hoursVariance.toFixed(2)} hrs (+{driver.variancePct.toFixed(1)}%) ⚠</span>
                        : driver.hoursVariance < -0.5
                        ? <span style={{ color: 'var(--hp-blue)', fontSize: '.76rem' }}>{driver.hoursVariance.toFixed(2)} hrs</span>
                        : <span style={{ color: 'var(--hp-green)', fontSize: '.76rem' }}>✓</span>}
                    </td>
                    <td>
                      {driver.hasDowntime
                        ? <span style={{ color: 'var(--hp-blue)', fontSize: '.76rem', fontWeight: 600 }}>{fmtHrs(driver.downtimeHrs)} ({driver.downtimePct.toFixed(1)}%)</span>
                        : <span style={{ color: 'var(--hp-dim)', fontSize: '.76rem' }}>—</span>}
                    </td>
                    <td><PphBadge pph={driver.pph} expected={driver.expectedPH} /></td>
                    <td style={{ fontWeight: 600 }}>{driver.hourlyAmt > 0 ? fmt(driver.hourlyAmt) : <span style={{ color: 'var(--hp-dim)' }}>—</span>}</td>
                    <td>
                      {driver.dirty
                        ? <span className="badge badge-amber" style={{ fontSize: '.66rem' }}>● Unsaved</span>
                        : driver.adjustedHours === driver.agreedHours && driver.agreedHours > 0
                        ? <span className="badge badge-green" style={{ fontSize: '.66rem' }}>Saved</span>
                        : driver.reportedHours === 0
                        ? <span className="badge" style={{ fontSize: '.66rem', background: 'rgba(100,100,100,.15)', color: 'var(--hp-dim)' }}>Pending</span>
                        : <span className="badge badge-amber" style={{ fontSize: '.66rem' }}>Unsaved</span>}
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <button className={`btn ${driver.dirty ? 'btn-primary' : 'btn-secondary'} btn-sm`} style={{ fontSize: '.7rem', padding: '3px 9px' }}
                        disabled={saving === driver.key} onClick={() => saveDriver(driver)}>
                        {saving === driver.key
                          ? <React.Fragment><RefreshCw size={10} style={{ animation: 'spin 1s linear infinite' }} /> Saving</React.Fragment>
                          : <React.Fragment><Save size={10} /> Save</React.Fragment>}
                      </button>
                    </td>
                  </tr>

                  {expanded === driver.key && (
                    <tr>
                      <td colSpan={14} style={{ padding: 0 }}>
                        <div style={{ background: 'rgba(201,168,76,.04)', borderTop: '1px solid rgba(201,168,76,.15)', padding: '16px 20px 16px 44px' }}>
                          <div className="stack-mobile" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

                            {/* Left: Hour reconciliation */}
                            <div>
                              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.6rem', textTransform: 'uppercase', color: 'var(--hp-dim)', marginBottom: 12 }}>Hour Reconciliation</div>
                              <div style={{ background: 'var(--hp-surface)', borderRadius: 8, padding: '14px 16px', fontSize: '.8rem' }}>
                                {csvStatus && (() => {
                                  const durationEntries = Object.entries(freshData?.duration || {}).filter(([k]) => k.startsWith(driver.key + '|'));
                                  const usualUsed = durationEntries.some(([, v]) => v.usualStartUsed);
                                  const sample = durationEntries[0]?.[1];
                                  return (
                                    <div style={{ marginBottom: 10, padding: '8px 10px', borderRadius: 6, background: usualUsed ? 'rgba(34,197,94,.07)' : 'rgba(251,191,36,.07)', border: `1px solid ${usualUsed ? 'rgba(34,197,94,.2)' : 'rgba(251,191,36,.2)'}`, fontSize: '.72rem' }}>
                                      <span style={{ fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.06em', color: usualUsed ? 'var(--hp-green)' : 'var(--hp-amber)', fontWeight: 700 }}>
                                        {usualUsed ? 'Start corrected from Drivers tab' : 'Using CSV start time'}
                                      </span>
                                      {sample && (
                                        <span style={{ color: 'var(--hp-dim)', marginLeft: 8 }}>
                                          {sample.startIndoor} → {sample.endOutdoor || sample.endIndoor}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })()}
                                {[
                                  { label: csvStatus ? 'Corrected Hours (period)' : 'Scanner Hours (period)', value: fmtHrs(driver.scannerHours), color: 'var(--hp-blue)' },
                                  { label: 'Driver Reported', value: driver.reportedHours > 0 ? fmtHrs(driver.reportedHours) : 'Not submitted', color: driver.flagged ? 'var(--hp-amber)' : 'var(--hp-text)' },
                                  { label: driver.hasDowntime ? 'Downtime (scanner − reported)' : 'Variance', 
                                    value: driver.hasDowntime 
                                      ? `${fmtHrs(driver.downtimeHrs)} (${driver.downtimePct.toFixed(1)}% of shift)`
                                      : driver.reportedHours > 0 ? (driver.hoursVariance >= 0 ? '+' : '') + driver.hoursVariance.toFixed(2) + ` hrs (${driver.variancePct.toFixed(1)}%)` : '—',
                                    color: driver.hasDowntime ? 'var(--hp-blue)' : driver.flagged ? 'var(--hp-amber)' : 'var(--hp-green)' },
                                ].map(row => (
                                  <div key={row.label} style={{ display: 'grid', gridTemplateColumns: '1fr auto', padding: '5px 0', borderBottom: '1px solid var(--hp-border)' }}>
                                    <span style={{ color: 'var(--hp-dim)' }}>{row.label}</span>
                                    <span style={{ color: row.color, fontWeight: 600 }}>{row.value}</span>
                                  </div>
                                ))}

                                <div style={{ marginTop: 12 }}>
                                  <label style={{ fontSize: '.7rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 4 }}>Driver Reported Hours</label>
                                  <input type="number" step="0.25" min="0" className="form-input" style={{ width: '100%', fontFamily: 'var(--font-mono)' }}
                                    value={driver.reportedHours}
                                    onChange={e => updateDriver(driver.key, 'reportedHours', e.target.value)} />
                                </div>
                                <div style={{ marginTop: 10 }}>
                                  <label style={{ fontSize: '.7rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 4 }}>
                                    Agreed Hours <span style={{ color: 'var(--hp-gold)' }}>(used for RCTI)</span>
                                  </label>
                                  <input type="number" step="0.25" min="0" className="form-input" style={{ width: '100%', fontFamily: 'var(--font-mono)', fontWeight: 600 }}
                                    value={driver.adjustedHours}
                                    onChange={e => updateDriver(driver.key, 'adjustedHours', e.target.value)} />
                                </div>
                                <div style={{ marginTop: 10 }}>
                                  <label style={{ fontSize: '.7rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 4 }}>Notes</label>
                                  <textarea className="form-input" rows={2} style={{ width: '100%', resize: 'vertical', fontSize: '.78rem' }}
                                    value={driver.notes}
                                    onChange={e => updateDriver(driver.key, 'notes', e.target.value)}
                                    placeholder={driver.flagged ? 'Reason for hours exceeding scanner...' : 'Notes...'} />
                                </div>
                                <button className={`btn ${driver.dirty ? 'btn-primary' : 'btn-secondary'} btn-sm`} style={{ marginTop: 10, width: '100%' }}
                                  disabled={saving === driver.key} onClick={() => saveDriver(driver)}>
                                  {saving === driver.key ? 'Saving...' : <React.Fragment><Save size={12} /> {driver.dirty ? 'Save Hours — unsaved' : 'Save Hours'}</React.Fragment>}
                                </button>
                              </div>

                              {/* Portal vs Scanner — day-by-day, so a driver whose portal
                                  hours run heavier than the scanner shows exactly which
                                  days (and by how much) drive the gap. */}
                              {(() => {
                                const p = portalByLogin[driver.key];
                                if (!p) return null;
                                const { rows: cmpRows, totPortal, totScan, netDelta } = buildDayCompare(driver.key);
                                const haveScan = Object.keys(scannerDaily).length > 0;
                                return (
                                  <div style={{ marginTop: 12 }}>
                                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.6rem', textTransform: 'uppercase', color: 'var(--hp-blue)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><Smartphone size={11} /> Portal vs Scanner — day by day</div>
                                    <div style={{ background: 'rgba(59,130,246,.06)', border: '1px solid rgba(59,130,246,.2)', borderRadius: 8, padding: '14px 16px', fontSize: '.8rem' }}>
                                      <div style={{ display: 'flex', gap: 16, marginBottom: 10, flexWrap: 'wrap' }}>
                                        {[
                                          { label: 'Portal', value: fmtHrs(totPortal), color: 'var(--hp-blue)' },
                                          ...(haveScan ? [
                                            { label: 'Scanner', value: fmtHrs(totScan), color: 'var(--hp-text)' },
                                            { label: 'Net Δ', value: (netDelta >= 0 ? '+' : '') + netDelta.toFixed(2), color: netDelta >= 0.5 ? 'var(--hp-red)' : netDelta <= -0.5 ? 'var(--hp-blue)' : 'var(--hp-green)' },
                                          ] : []),
                                        ].map(s => (
                                          <div key={s.label}>
                                            <div style={{ fontSize: '.58rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--hp-dim)' }}>{s.label}</div>
                                            <div style={{ fontSize: '1rem', fontWeight: 800, color: s.color }}>{s.value}</div>
                                          </div>
                                        ))}
                                      </div>
                                      {!haveScan && (
                                        <div style={{ fontSize: '.68rem', color: 'var(--hp-dim)', marginBottom: 8 }}>Load data to line up scanner hours against portal.</div>
                                      )}
                                      {cmpRows.length > 0 && (
                                        <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 10 }}>
                                          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto auto auto', gap: 8, padding: '2px 0 4px', fontSize: '.6rem', textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--hp-dim)', borderBottom: '1px solid var(--hp-border)' }}>
                                            <span>Date</span><span>Portal start → finish</span><span style={{ textAlign: 'right' }}>Ptl</span><span style={{ textAlign: 'right' }}>Scan</span><span style={{ textAlign: 'right' }}>Δ</span>
                                          </div>
                                          {cmpRows.map(r => {
                                            const dColor = r.delta == null ? 'var(--hp-dim)' : r.delta >= 0.5 ? 'var(--hp-red)' : r.delta <= -0.5 ? 'var(--hp-blue)' : 'var(--hp-green)';
                                            return (
                                              <div key={r.date} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto auto auto', gap: 8, padding: '3px 0', borderBottom: '1px solid var(--hp-border)', fontSize: '.74rem' }}>
                                                <span style={{ color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>{r.date.slice(5)}</span>
                                                <span>{r.hasPortal ? `${r.portalStart || '—'} → ` : <span style={{ color: 'var(--hp-dim)' }}>no portal log</span>}{r.hasPortal && (r.portalOpen ? <span style={{ color: 'var(--hp-amber)' }}>open</span> : (r.portalFinish || ''))}</span>
                                                <span style={{ fontWeight: 600, textAlign: 'right' }}>{r.hasPortal && r.portalFinish ? r.portalHrs.toFixed(2) : '—'}</span>
                                                <span style={{ textAlign: 'right', color: r.hasScan ? 'var(--hp-text)' : 'var(--hp-dim)' }} title={r.hasScan && (r.scanStart || r.scanFinish) ? `${r.scanStart || '?'} → ${r.scanFinish || '?'}` : ''}>{r.hasScan ? r.scanHrs.toFixed(2) : (haveScan ? '—' : '·')}</span>
                                                <span style={{ textAlign: 'right', fontWeight: 700, color: dColor }}>{r.delta == null ? '—' : (r.delta >= 0 ? '+' : '') + r.delta.toFixed(2)}</span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                      <button className="btn btn-secondary btn-sm" style={{ width: '100%' }} disabled={!(p.hours > 0)}
                                        onClick={() => updateDriver(driver.key, 'adjustedHours', p.hours)}>
                                        <Clock size={12} /> Use portal hours as agreed ({fmtHrs(p.hours)})
                                      </button>
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Downtime panel — only when scanner > reported */}
                              {driver.hasDowntime && (
                                <div style={{ marginTop: 12 }}>
                                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.6rem', textTransform: 'uppercase', color: 'var(--hp-blue)', marginBottom: 8 }}>Downtime Categorisation</div>
                                  <div style={{ background: 'rgba(59,130,246,.06)', border: '1px solid rgba(59,130,246,.2)', borderRadius: 8, padding: '14px 16px', fontSize: '.8rem' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', padding: '4px 0', marginBottom: 8 }}>
                                      <span style={{ color: 'var(--hp-dim)' }}>Unaccounted time</span>
                                      <span style={{ color: 'var(--hp-blue)', fontWeight: 700 }}>{fmtHrs(driver.downtimeHrs)} ({driver.downtimePct.toFixed(1)}% of shift)</span>
                                    </div>
                                    <div style={{ marginBottom: 10 }}>
                                      <label style={{ fontSize: '.7rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 4 }}>Category</label>
                                      <select className="form-select" style={{ width: '100%' }}
                                        value={downtimeState[driver.key]?.category || ''}
                                        onChange={e => updateDowntime(driver.key, 'category', e.target.value)}>
                                        <option value="">— Select category —</option>
                                        {DOWNTIME_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                      </select>
                                    </div>
                                    <div style={{ marginBottom: 10 }}>
                                      <label style={{ fontSize: '.7rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '.08em', display: 'block', marginBottom: 4 }}>Notes</label>
                                      <textarea className="form-input" rows={2} style={{ width: '100%', resize: 'vertical', fontSize: '.78rem' }}
                                        value={downtimeState[driver.key]?.notes || ''}
                                        onChange={e => updateDowntime(driver.key, 'notes', e.target.value)}
                                        placeholder="Additional context..." />
                                    </div>
                                    <button className="btn btn-primary btn-sm" style={{ width: '100%' }}
                                      disabled={savingDowntime === driver.key} onClick={() => saveDowntime(driver)}>
                                      {savingDowntime === driver.key
                                        ? <React.Fragment><RefreshCw size={10} style={{ animation: 'spin 1s linear infinite' }} /> Saving...</React.Fragment>
                                        : <React.Fragment><Clock size={10} /> Log Downtime</React.Fragment>}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Right: Performance */}
                            <div>
                              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '.6rem', textTransform: 'uppercase', color: 'var(--hp-dim)', marginBottom: 12 }}>Performance</div>
                              <div style={{ background: 'var(--hp-surface)', borderRadius: 8, padding: '14px 16px', fontSize: '.8rem' }}>
                                {[
                                  { label: 'Total Parcels (billed)', value: driver.totalParcels.toLocaleString() },
                                  { label: 'Onboarded', value: driver.onboarded ? driver.onboarded.toLocaleString() : '—' },
                                  { label: 'Express', value: (driver.qty.Exp || 0).toLocaleString() },
                                  { label: 'Depot Bags', value: (driver.qty['Depot Bags'] || 0).toLocaleString() },
                                  { label: 'Agreed Hours', value: driver.adjustedHours > 0 ? fmtHrs(driver.adjustedHours) : '—', bold: true },
                                  { label: 'Onboarded / Hr', value: driver.onboardedPph > 0 ? driver.onboardedPph.toFixed(1) : '—', bold: true },
                                  { label: 'Parcels/Hr (billed)', value: driver.pph > 0 ? driver.pph.toFixed(1) : '—',
                                    color: driver.expectedPH && driver.pph > 0 ? (driver.pph < driver.expectedPH * 0.7 ? 'var(--hp-red)' : driver.pph < driver.expectedPH * 0.9 ? 'var(--hp-amber)' : 'var(--hp-green)') : undefined },
                                  { label: 'Expected P/Hr', value: driver.expectedPH ? driver.expectedPH.toFixed(0) : '—', color: 'var(--hp-dim)' },
                                ].map(row => (
                                  <div key={row.label} style={{ display: 'grid', gridTemplateColumns: '1fr auto', padding: '5px 0', borderBottom: '1px solid var(--hp-border)' }}>
                                    <span style={{ color: 'var(--hp-dim)' }}>{row.label}</span>
                                    <span style={{ fontWeight: row.bold ? 700 : 400, color: row.color || 'var(--hp-text)' }}>{row.value}</span>
                                  </div>
                                ))}

                                {/* Inflation risk panel */}
                                {driver.flagged && (
                                  <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 6, background: 'rgba(251,191,36,.08)', border: '1px solid rgba(251,191,36,.2)' }}>
                                    <div style={{ fontSize: '.68rem', fontFamily: 'var(--font-mono)', color: 'var(--hp-amber)', textTransform: 'uppercase', marginBottom: 4 }}>⚠ Inflation Risk</div>
                                    <div style={{ fontSize: '.78rem', color: 'var(--hp-text)' }}>
                                      Reported hours exceed scanner by <strong>{driver.hoursVariance.toFixed(2)} hrs ({driver.variancePct.toFixed(1)}%)</strong>. Add a reason in the notes field.
                                    </div>
                                  </div>
                                )}

                                {/* RCTI preview */}
                                <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 6, background: 'rgba(201,168,76,.08)', border: '1px solid rgba(201,168,76,.2)' }}>
                                  <div style={{ fontSize: '.68rem', fontFamily: 'var(--font-mono)', color: 'var(--hp-dim)', textTransform: 'uppercase', marginBottom: 6 }}>RCTI Line Item Preview</div>
                                  {driver.adjustedHours > 0 ? (
                                    <React.Fragment>
                                      <div style={{ fontSize: '.78rem', color: 'var(--hp-text)' }}>Hourly Service Fee — {driver.adjustedHours} hrs × {fmt(driver.hourlyRate)}/hr</div>
                                      <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--hp-gold)', marginTop: 4 }}>
                                        {fmt(driver.hourlyAmt)} <span style={{ fontSize: '.7rem', fontWeight: 400, color: 'var(--hp-dim)' }}>ex GST</span>
                                      </div>
                                    </React.Fragment>
                                  ) : (
                                    <div style={{ fontSize: '.78rem', color: 'var(--hp-dim)' }}>Enter agreed hours to preview</div>
                                  )}
                                </div>
                              </div>
                            </div>

                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {drivers.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--hp-dim)' }}>
              <Clock size={36} style={{ marginBottom: 12, opacity: .3 }} />
              <p style={{ fontSize: '.875rem' }}>No hourly drivers loaded — click Load Hourly Data</p>
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
    </React.Fragment>
  );
}
